import React, { useState, useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { getProjects } from "../../../features/settings";
import { setMessageState } from "../../../features/messageInfo";
import { getData, postData, putData } from "../../../core/fetchService";
import { CheckSampleContent } from "../../components/checkSample";
import { MESSAGE_STATUS, BTN, NLU_TYPE_AC_OPTS } from "../../../core/constants";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core";
import YAML from "yaml";
import { Controlled as CodeMirror } from "react-codemirror2";
import { EditConfig } from "../../components/editConfig";
import CloseBar from "../../components/dialogCloseBar";
import MultiSelect from "../../components/material-ui/MultiSelect";
import {
  useTranslation, showInfo, showError, ucfirst, getRunConfigOption, filterName,
  getProject, isProperType, isProperRuntimeType, isGAI, isASR,
  subtractObject, qw, unique, mapObject, renameKeys,
} from "../../../core/utils";
import notBackdropClicked from "../../components/helpers/notBackdropClicked";
import ConfirmDialog from "../../components/confirmDialog";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import IconButton from "../../components/material-ui/IconButton";
import CircularProgress from '@material-ui/core/CircularProgress';

import { BackendSettings } from "../../pages/dashboard/SettingsDialog";

const NLUTypes = {
  NLU_RASA: "nluConfig",
  NLU_NB: "nluConfig",
  ASR_E2E: "nluConfig",
};

const useStyles = makeStyles(theme => {
  const fieldset = {
    margin: "10px",
    padding: "20px",
    borderRadius: 5,
    borderWidth: 1,
  };
  const dialogFieldMargin = {
    margin: 8,
  };
  const dialogFieldPadding = {
    paddingRight: 17,
  };
  return {
    _fieldset: {
      ...fieldset,
    },
    fieldset: {
      ...fieldset,
      color: "#C4C4C4",
    },
    dialogFieldMargin,
    dialogField: {
      ...dialogFieldMargin,
      ...dialogFieldPadding,
    },
    dialogFieldExt: {
      margin: "8px 0px 8px 8px",
      ...dialogFieldPadding,
    },
    dialogInnerField: dialogFieldPadding,
    legend: {
      color: "#757575"
    },
    checkBox: {
      marginLeft: 10,
    },
    inlineFloatRightIcon: {
      float: "right",
      marginLeft: "20px",
      marginTop: "-25px",
      marginRight: "-15px",
      visibility: "hidden",
      '*:hover > &': {
        visibility: "visible",
      },
    },
    textFieldMessage: {
      marginTop: -5,
      marginBottom: 13,
    },
    errorMessage: {
      color: 'red',
    },
    runtimeModelStatus: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      color: 'initial',
    },
    runtimeModelButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    checkSampleFirstRow: {
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      margin: '0 10px',
    },
  }
});

const useModalEditConfig = () => {
  const [isShowingEditConfig, setIsShowingEditConfig] = useState(false);

  function toggleEditConfig() {
    setIsShowingEditConfig(!isShowingEditConfig);
  }

  return {
    isShowingEditConfig,
    toggleEditConfig,
  };
};

function EditDialog(props) {
  const {
    _id, editModel, isClone, configDefaults, onClose,
    backends, projectId, projects, modelFilters, modelConfigs,
  } = props;

  const toType = ext_type => ext_type?.replace(/_Runtime/, '');
  const toExtType = type => type +'_Runtime';

  const EXT_TYPES = ['NLU_NB', 'ASR_E2E'];
  const EXT_TYPE_OPTS = EXT_TYPES.map(t => ({ name: t, value: toExtType(t) }));

  const classes = useStyles();
  const project = getProject(projects, projectId);

  const is_genv = isGAI(project);
  const is_asr = isASR(project);

  const filterInternalModelTypeACOpts = (project, ac_opts) => ac_opts.filter(o => isProperType(project, o.value));
  const filterExternalModelTypeACOpts = (project, ac_opts) => ac_opts.filter(o => isProperRuntimeType(project, o.value));
  const internalModelTypeACOpts = filterInternalModelTypeACOpts(project, NLU_TYPE_AC_OPTS);
  const externalModelTypeACOpts = filterExternalModelTypeACOpts(project, EXT_TYPE_OPTS);
  const getACValue = (opts, value) => opts.length == 1 ? opts[0] : value;

  const getDefaultValue = (type, name) =>
    type ? configDefaults?.[NLUTypes[type]]?.[name] : '';

  const is_external_init = false;
  const ext_type_init = is_external => is_external ? getACValue(externalModelTypeACOpts)?.value : undefined;
  const type_init = is_external => is_external
    ? toType(ext_type_init(is_external)) : getACValue(internalModelTypeACOpts)?.value;
  const type_deps_init = (type, editModelParams = {}) => ({
    type,
    nlu_train_url: editModelParams.nlu_train_url || getDefaultValue(type, 'train_url'),
    nlu_deploy_url: editModelParams.nlu_deploy_url || getDefaultValue(type, 'deploy_url'),
    model_config: type == editModelParams?.type ? editModelParams.model_config : null,
  });

  const old_url_model_field = { training: 'nlu_train_url', runtime: 'nlu_deploy_url' };
  const old_url_setgs_field = { training: 'train_url',     runtime: 'deploy_url' };

  const default_be_value = (editModel, type) => {
    const old_url = editModel?.[old_url_model_field[type]]?.trim();
    const old_default_url = configDefaults?.nluConfig?.[old_url_setgs_field[type]]?.trim();
    return {
      [type]:
        editModel?.nluConfig?.[type]
        || backends?.find(b => b.url == old_url)?._id
        || !old_url && (configDefaults?.nluConfig?.[type]
                        || backends?.find(b => b.url == old_default_url)?._id)
        || undefined,
    };
  };

  const initialEditModel = {
    project: projectId,
    name: null,
    description: '',
    is_external: is_external_init,
    trainSet: [],
    nluConfigText: null,
    ext_type: ext_type_init(is_external_init),
    ...type_deps_init(type_init(is_external_init)),
    nluConfig: {
      ...default_be_value(editModel, 'training'),
      ...default_be_value(editModel, 'runtime'),
    },
    include_data_schema: true,
    check_dupes: false,
    remote_name: '',
    runtime_name: '',
  };

  const MISSING = { missing: true };
  const LOADING = { loading: true };
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const defaultRuntimeName = editModel => (editModel.remote_name || '').replace(/__u\p{Hex_Digit}{7}$/u, '');
  const assignRuntimeName = editModel =>
    isClone ? { runtime_name: undefined }
      : editModel.runtime_name == undefined ? { runtime_name: defaultRuntimeName(editModel) }
      : {};

  const _editModel = editModel && {
    ...editModel,
    ...assignRuntimeName(editModel),
  };

  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isPreviewConfigOpen, setIsPreviewConfigOpen] = useState(false);
  const [editModelParams, setEditModelParams] = useState(_editModel || initialEditModel);
  const [trainServerInfo, setTrainServerInfo] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [showCheckSample, setShowCheckSample] = useState(false);
  const [checkSampleText, setCheckSampleText] = useState("");
  const [checkSampleInProgress, setCheckSampleInProgress] = useState(false);
  const [checkSampleResult, setCheckSampleResult] = useState();
  const [_runtimeModelInfo, setRuntimeModelInfo] = useState(MISSING);

  const { isShowingEditConfig, toggleEditConfig } = useModalEditConfig();
  const selectedFilters = editModelParams.filters
    ?.map(f => modelFilters?.find(mf => mf._id == f.filter)) || [];

  const _rti = _runtimeModelInfo;
  const runtimeModelInfo =  {
    ..._rti,
    date: _rti.date ? new Date(_rti.date).toLocaleString() : '???',
    by_other: _rti.by_other ? t('train.by_other') : '',
  };

  const is_nb = ['NLU_RASA', 'NLU_NB'].includes(editModelParams.type);
  const is_ext_nb = editModelParams.ext_type == 'NLU_NB_Runtime';
  const is_ext_asr = editModelParams.ext_type == 'ASR_E2E_Runtime';

  const getServerUrl = type => backends?.find(b => b._id == editModelParams.nluConfig?.[type])?.url
    ?? editModelParams[old_url_model_field[type]];
  const getRuntimeServerUrl = () => getServerUrl('runtime');
  const getTrainServerUrl = () => getServerUrl('training');

  useEffect(() => {
    updateExtModelList(undefined, true);
  }, [backends, editModelParams.is_external]);

  useEffect(() => {
    setEditModelParams(p => ({
      ...p,
      nluConfig: {
        ...default_be_value(p, 'training'),
        ...default_be_value(p, 'runtime'),
      },
    }));
  }, [backends, configDefaults]);

  const EMPTY_EXT_MODEL_LIST = { models: [] };

  const [savedValue, setSavedValue] = useState();
  const [extModelList, setExtModelList] = useState(EMPTY_EXT_MODEL_LIST);

  const not_available = Boolean(extModelList.error || runtimeModelInfo.missing
                                || runtimeModelInfo.loading || !editModelParams.runtime_name);

  const updateExtModelList = async (server, no_error_popup) => {
    const is_ext = editModelParams.is_external;
    if (!is_ext && !is_nb || is_ext && !is_ext_nb)
      return;
    const getAdminUrl = b => b ? b.adminUrl || { error_server_name: b.name } : undefined;
    const server_admin_url = getAdminUrl(server)
      || getAdminUrl(backends?.find(b => b._id == editModelParams?.nluConfig?.runtime))
      || editModelParams.nlu_deploy_url;
    if (server_admin_url && typeof server_admin_url != 'string')
      return showError(dispatch, t)('settings.error_no_admin_url', t('backends.admin_url'), server_admin_url.error_server_name);
    let d = [];
    if (server_admin_url) {
      const url = `/api/runtime/list_models?server=${server_admin_url}&type=${editModelParams.type}&is_mgmt=true`;
      const r = await getData(url, dispatch, data => {
        d = data;
        setExtModelList(data ? { models: data } : EMPTY_EXT_MODEL_LIST);
      }, {
        on_error: e => { if (no_error_popup) throw e },
      }).catch(e => {
        console.error(e);
        return { error: e };
      });
      if (r.error) {
        d = [];
        setExtModelList({ error: r.error.message });
      }
    }
    if (is_ext) {
   // if (!d.includes(editModelParams.remote_name))
   //   setEditModelParams({ ...editModelParams, remote_name: '' });
    } else {
      updateRuntimeModelInfo(no_error_popup);
    }
  };

  const updateRuntimeModelInfo = no_error_popup => {
    const { runtime_name } = editModelParams;
    const server_url = getRuntimeServerUrl();
    if (!server_url || !runtime_name)
      return;
    const url = `/api/runtime/model_info?server=${server_url}&name=${runtime_name}&id=${_id}`;
    setRuntimeModelInfo(LOADING);
    getData(url, dispatch, setRuntimeModelInfo, {
      on_error: e => { if (no_error_popup) throw e },
    }).catch(e => {
      console.error(e);
      setRuntimeModelInfo(MISSING);
    });
  }

  const handleUnloadModel = () => {
    const { runtime_name } = editModelParams;
    const server_url = getRuntimeServerUrl();
    const url = `/api/runtime/unload_model?server=${server_url}&name=${runtime_name}`;
    postData(url, {}, dispatch, setRuntimeModelInfo, { on_error: updateRuntimeModelInfo });
  };

  const handleChange = (event) => {
    const { id, value } = event.target || {};
    if (id && value != undefined) {
      setEditModelParams((p) => ({
        ...p,
        [id]: value,
      }));
    }
  };

  const handleCheckboxChange = id => event => {
    const is_checked = event.target.checked;
    const type = id == 'is_external' ? type_init(is_checked) : undefined;
    const other = id == 'is_external' ? {
      ext_type: ext_type_init(is_checked),
      ...type_deps_init(type, editModelParams),
    } : {};
    setEditModelParams({ ...editModelParams, [id]: is_checked, ...other });
  };

  const handleChangeTrainSet = (event, value, no_trainset) => {
    setEditModelParams({ ...editModelParams, trainSet: value.map(v => v._id), no_trainset })
  };

  const handleChangeConfig = (event, value) => {
    setEditModelParams({ ...editModelParams, model_config: value?._id || null })
  };

  const handleChangeNluType = (_____, value) => {
    setEditModelParams({ ...editModelParams, ...type_deps_init(value?.value, editModelParams) });
  };

  const handleChangeFilters = (event, value) => {
      setEditModelParams({
        ...editModelParams, 
        filters: (value || []).map(({ _id }, index) => ({
          filter: _id,
          index,
        })),
      })
  };

  const handleTextFieldFocus = event => setSavedValue(event.target.value);
  const handleTextFieldBlur = action => event => {
    const value = event.target.value;
    if (value != savedValue) {
      setSavedValue(value);
      if (value)
        action(value);
    }
  };

  const handleRuntimeNameChange = handleTextFieldBlur(updateRuntimeModelInfo);

  const closeEditConfig = () => { toggleEditConfig() };
  const doCancel = () => { onClose(BTN.CANCEL) };

  const doGetSchema = () => {
    return postData(`/api/model/get_data_schema`, {
      project: projectId,
      filters: editModelParams.filters,
    }, dispatch, data => {
      setEditModelParams({ ...editModelParams, nluConfigText: YAML.stringify(data) || null });
    });
  };

  const doSave = async () => {
    const save = {
      method: _id && !isClone? putData : postData,
      uri: _id ? `/api/model${isClone ? '/copy' : ''}/${_id}` : `/api/model`,
    };
    const model = {
      ...subtractObject(editModelParams, editModel || {}),
      trainSet: editModelParams.trainSet.filter((id) => id != NO_TRAINSET_ID),
    };

    await save.method(
      save.uri,
      { model },
      dispatch,
      data => {
        dispatch(
          setMessageState({
            snackBarMessages: t("common.save_success"),
            snackBarVariant: MESSAGE_STATUS.SUCCESS,
            snackBarState: true,
          }),
        );
        // Success message only in one place (if needed will be deleted)

        onClose(BTN.SAVE);
      }
    );
  };

  const getModelConfig = id => setSelectedConfig(modelConfigs.find(c => c._id == id));

  const handlePreviewConfig = () => {
    setIsPreviewConfigOpen(!isPreviewConfigOpen);
  };

  const onPressEnter = action => event => {
    if (event.key == 'Enter')
      action(event);
  };

  const NO_TRAINSET_ID = '__no_trainset__';
  const NO_TRAINSET_OPTION = is_genv ? [{ name: t('train.no_trainset'), _id: NO_TRAINSET_ID }] : [];
  const trainSet = editModelParams.no_trainset
    ? NO_TRAINSET_OPTION
    : ((project.datasets || []).map(({ name, _id }) => ({ name, _id}))
       .concat((editModelParams.trainSet.length ? [] : NO_TRAINSET_OPTION)));
  const _handleChangeTrainSet = (event, value) =>
    handleChangeTrainSet(event, value, value[0]?._id == NO_TRAINSET_ID);

  const addFilters = (a, f) => {
    return a.excludeIntents;
  };
  const filterKey = (a,n) => a + ucfirst(n) +'s';
  const json = v => JSON.stringify(v, null, 2).replace(new RegExp('"', 'g'), '');
  const showFilter = f =>
    qw`intent slot`.map(n => qw`exclude replace`.map(a => {
      const key = filterKey(a,n);
      const v = f[key]; // TODO: replace with getIntentOrSlotName(filter[n][a])
      return v && (Array.isArray(v) ? v : Object.keys(v)).length && key +': '+ json(v);
    })).flat().filter(Boolean).join('\n');
  const showFiltersContent = fs => fs ? <pre>
    {showFilter(fs.reduce((a, f, i) => {
      qw`intent slot`.forEach(n => {
        let key = filterKey('exclude', n);
        a[key] = unique((a[key] || []).concat(f[key] || []));
        key = filterKey('replace', n);
        a[key] = {...a[key], ...renameKeys(f[key] || {}, mapObject(a[key] || {}, (_, k) => {
          // show repeating keys with different values with prefix
          k = k.replace(/^#\d+__/, ''); // remove prefix before comparing
          return (a[key][k] != f[key][k] ? '#'+ i +'__' : '') + k;
        }))};
      });
      return a;
    }, {}))}
  </pre> : null;

  const internal = () => (
    <>
      <fieldset className={classes.fieldset}>
        <legend className={classes.legend}>{t('train.data')}</legend>
        <Grid container spacing={3}>
          <Grid item sm={12}>
            <MultiSelect
              id="trainSet"
              fullWidth
              required
              options={trainSet}
              onChange={_handleChangeTrainSet}
              getOptionLabel={option => option?.name}
              value={editModelParams.no_trainset ? NO_TRAINSET_OPTION
                : editModelParams.trainSet.map(id => trainSet.find(ds => ds._id == id))}
              label={t("train.train_set")}
            />
          </Grid>
          {!is_genv && !is_asr && (
            <Grid item sm={12}>
              <div style={{ display: "flex" }}>
                <MultiSelect
                  style={{ flexGrow: 1, marginRight: 20 }}
                  id="filters"
                  required
                  filterSelectedOptions
                  options={modelFilters || []}
                  onChange={handleChangeFilters}
                  getOptionLabel={option => option.name}
                  value={selectedFilters}
                  label={t("train.filters")}
                />
                <Button
                  id="showFilters"
                  variant="outlined"
                  disabled={!selectedFilters.length}
                  onClick={() => setShowFilters(true)}
                >
                  {t("train.preview_filters")}
                </Button>
              </div>
              <div style={{ marginTop: "24px" }}>
                {editModelParams.type == 'NLU_RASA' ?
                  <FormControlLabel
                    style={{color: 'initial'}}
                    control={<Checkbox/>}
                    label={t("train.include_data_schema")}
                    checked={editModelParams.include_data_schema === undefined  // for old models
                        || editModelParams.include_data_schema}
                    onChange={handleCheckboxChange('include_data_schema')}
                  /> : null}
                <Button
                  id="btnEdit"
                  variant="outlined"
                  onClick={() => {
                    doGetSchema().then(() => toggleEditConfig());
                  }}
                >
                  {t("train.preview_data_schema")}
                </Button>
              </div>
            </Grid>
          )}
        </Grid>
      </fieldset>
      <fieldset className={classes.fieldset}>
        <legend className={classes.legend}>{t("common.train_n")}</legend>
        <Autocomplete
          id="type"
          size="small"
          fullWidth
          required
          className={classes.dialogField}
          options={internalModelTypeACOpts}
          onChange={handleChangeNluType}
          getOptionLabel={(option) => option.name || ""}
          value={getACValue(internalModelTypeACOpts, NLU_TYPE_AC_OPTS.find(item => item.value === editModelParams.type) || null)}
          getOptionSelected={(option, value) => option.value === value.value || true}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" label={t("train.nlu_type")}/>
          )}
          disabled={Boolean(_id) && !isClone || internalModelTypeACOpts.length == 1}
        />
        {editModelParams.type == "ASR_E2E" && (
          <FormGroup style={{ marginLeft: 8 /* as in dialogField */ }}>
            <FormControlLabel
              // className={classes.dialogField}
              control={<Checkbox />}
              label={t("train.check_dupes")}
              checked={editModelParams.check_dupes}
              onChange={(event) => {
                setEditModelParams({
                  ...editModelParams,
                  check_dupes: event.target.checked,
                });
              }}
            />
          </FormGroup>
        )}
        {editModelParams.type && modelConfigs && (
          <div style={{ display: "flex" }}>
            <Autocomplete
              style={{ flexGrow: 1 }}
              options={
                modelConfigs.filter((c) => c.type == editModelParams.type) || []
              }
              groupBy={(option) => option.scope}
              id="config"
              required
              size="small"
              label={"Config"}
              onChange={handleChangeConfig}
              variant="outlined"
              getOptionLabel={(option) => option.name || ""}
              value={
                modelConfigs.find(
                  (c) => c._id == editModelParams.model_config
                ) || null
              }
              getOptionSelected={(option, value) =>
                option._id === value._id || true
              }
              renderInput={(params) => (
                <TextField
                  className={classes.dialogField}
                  required
                  {...params}
                  variant="outlined"
                  label={t("train.config")}
                />
              )}
            />
            <Button
              disabled={!editModelParams.model_config}
              variant="outlined"
              className={classes.dialogField}
              style={{
                width: 114 /* manually adjusted in order to the PREVIEW button be the same size as the INFO button in BackendSettings below */,
              }}
              onClick={() => {
                getModelConfig(editModelParams.model_config);
                handlePreviewConfig();
              }}
            >
              {t("train.preview_config")}
            </Button>
          </div>
        )}
        {editModelParams.type && (
          <BackendSettings
            id="nlu"
            typeOfInput="autocomplete"
            fields={["train_url"]}
            settings={{
              ...editModelParams,
              train_url: editModelParams.nlu_train_url,
            }}
            setSettings={() => (_, value) => {
              setEditModelParams(p => ({
                ...p,
                nluConfig: { ...editModelParams.nluConfig, training: value._id },
              }));
            }}
            fieldDescr={{
              train_url: {
                type: "nb_server",
                props: { id: "training", label_key: "train_url" },
                classes: classes.dialogFieldMargin,
                info: true,
                classes_info: classes.dialogField,
                scope: settings => settings.nluConfig,
              },
            }}
            backends={backends}
          />)
        }
      </fieldset>
      {["NLU_RASA", "NLU_NB"].includes(editModelParams.type) && (
        <fieldset className={classes.fieldset}>
          <legend className={classes.legend}>{t("common.runtime")}</legend>
          <BackendSettings
            id="nlu"
            typeOfInput="autocomplete"
            fields={["deploy_url"]}
            settings={{
              ...editModelParams,
              deploy_url: editModelParams.nlu_deploy_url,
            }}
            setSettings={() => (_, value) => {
              setEditModelParams(p => ({
                ...p,
                nluConfig: { ...editModelParams.nluConfig, runtime: value._id },
              }));
            }}
            fieldDescr={{
              deploy_url: {
                type: "nb_runtime",
                props: { id: "runtime", label_key: "deploy_url" },
                classes: classes.dialogFieldMargin,
                info: true,
                classes_info: classes.dialogField,
                scope: settings => settings.nluConfig,
              },
            }}
            backends={backends}
          />
          <TextField
            id="runtime_name"
            label={t("train.runtime_name")}
            className={classes.dialogField}
            size="small"
            variant="outlined"
            fullWidth
            value={editModelParams.runtime_name}
            onChange={handleChange}
            onFocus={handleTextFieldFocus}
            onBlur={handleRuntimeNameChange}
            onKeyDown={onPressEnter(handleRuntimeNameChange)}
          />
          <div className={classes.runtimeModelStatus}>
            <div className={classes.legend +' '+ classes.dialogField} style={{overflow: 'hidden', width: '40%', padding: 0}}>
              {runtimeModelInfo.loading ? <CircularProgress size={27} style={{marginLeft: 10}}/>
              : runtimeModelInfo.missing ? t('train.not_loaded') : t('train.loaded_as', runtimeModelInfo)}
            </div>
            <div className={classes.runtimeModelButtons}>
            <Button
              disabled={not_available}
              variant="outlined"
              className={classes.dialogField}
              style={{ height: "40px", marginTop: "8px" }}
              onClick={() => setShowCheckSample(true)}
            >
              {t("train.show_check_sample")}
            </Button>
            <Button
              disabled={not_available}
              variant="outlined"
              className={classes.dialogField}
              style={{ height: "40px", marginTop: "8px" }}
              onClick={handleUnloadModel}>
              {t("train.unload_runtime_model")}
            </Button>
          </div>
          </div>
        </fieldset>
      )}
    </>
  );

  const handleChangeExtType = (event, value) => {
    const ext_type = value?.value || null;
    const type = toType(ext_type);

    setEditModelParams({ ...editModelParams, type, ext_type })
  };

  const onExtModelChange = name => (event, value) => {
    setEditModelParams({ ...editModelParams, [name]: value });
  };

  const external = () => {
    return (<>
      <fieldset className={classes.fieldset}>
        <legend className={classes.legend}>{t('train.ext_model')}</legend>
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <Autocomplete
              id="ext_type"
              size="small"
              fullWidth
              required
              options={externalModelTypeACOpts}
              onChange={handleChangeExtType}
              getOptionLabel={option => option.name || ""}
              value={getACValue(externalModelTypeACOpts, EXT_TYPE_OPTS.find(item => item.value == editModelParams.ext_type) || null)}
              getOptionSelected={(option, value) => option.value == value.value || true}
              renderInput={params => (
                <TextField {...params} variant="outlined" label={t("train.ext_type")}/>
              )}
              disabled={externalModelTypeACOpts.length == 1}
            />
          </Grid>
          {editModelParams.ext_type && (<>
            <Grid item sm={12}>  
              <BackendSettings
                id="nlu"
                typeOfInput="autocomplete"
                fields={["deploy_url"]}
                settings={{ ...editModelParams, deploy_url: editModelParams.nlu_deploy_url }}
                setSettings={() => (_, value) => {
                  setEditModelParams(p => ({
                    ...p,
                    nluConfig: { ...editModelParams.nluConfig, runtime: value._id },
                  }));
                }}
                fieldDescr={{
                  deploy_url: {
                    type: "nb_runtime",
                    props: { id: "runtime", label_key: "deploy_url" },
                    info: true,
                    classes_info: classes.dialogFieldExt,
                    scope: settings => settings.nluConfig,
                    onChange: updateExtModelList, // TODO: can we use setSettings for that?
                  },
                }}
                backends={backends}
              />
            </Grid>
            <Grid item sm={12}>
              {!is_ext_nb &&
                <TextField
                  id="remote_name"
                  required
                  size="small"
                  label={t("train.ext_name")}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  value={editModelParams.remote_name}
                  inputProps={{ readOnly: !is_ext_asr }}
                />}
              {is_ext_nb &&
                <Autocomplete
                  required
                  id="remote_name"
                  options={extModelList.models || []}
                  onChange={onExtModelChange("remote_name")}
                  value={editModelParams.remote_name || null}
                  renderInput={params =>
                    <TextField {...params} variant="outlined" label={t("train.ext_name")}/>
                  }
                  fullWidth
                  size="small"
                />}
            </Grid>
          </>)}
        </Grid>
      </fieldset>
    </>);
  };

  return (
      <Dialog
        maxWidth="sm"
        fullWidth={true}
        open={true}
        onClose={notBackdropClicked(doCancel)}
        scroll="paper"
        aria-labelledby="edit-model-dialog-title"
      >
        <EditConfig
          isOpen={isShowingEditConfig}
          onClose={closeEditConfig}
          nluConfigText={editModelParams.nluConfigText}
          mode="yaml"
          title={t("train.data_schema")}
        />
        {showCheckSample && <ConfirmDialog
          title={t("train.check_sample", { name: editModelParams.name || '<unnamed>' })}
          content={<CheckSampleContent
            classes={classes}
            modelParams={editModelParams}
            options={{ is_ext_asr, is_ext_nb, is_nb, server_url: getRuntimeServerUrl() }}
          />}
          showBtnNameAgree={false}
          btnNameDisagree={t("common.close")}
          closeModal={() => setShowCheckSample(false)}
          maxWidth='md'
        />}
        {showFilters && <ConfirmDialog
          title={t('train.filters')}
          content={showFiltersContent(selectedFilters)}
          showBtnNameAgree={false}
          closeModal={() => setShowFilters(false)}
          btnNameDisagree={t("common.close")}
          maxWidth='md'
        />}

        <CloseBar onClose={doCancel} title={_id ? isClone ? t("train.clone") : t("train.edit") : t("train.new")}/>
        <DialogContent dividers={true}>
          {isPreviewConfigOpen &&
          <Dialog
            fullWidth={true}
            open={isPreviewConfigOpen}
            onClose={handlePreviewConfig}
            scroll="paper"
            maxWidth="sm"
          >
            <CloseBar onClose={handlePreviewConfig} title={selectedConfig?.name}/>
            <CodeMirror
              value={selectedConfig?.data}
              options={{
                mode: "yaml",
                lineNumbers: true,
              }}
              readOnly
            />
          </Dialog>
          }
          <TextField
            id="name"
            required
            size="small"
            className={classes.dialogField}
            label={t("train.model_name")}
            onChange={filterName(handleChange)}
            variant="outlined"
            fullWidth
            value={editModelParams.name || ""}
          />
          <TextField
            id="description"
            size="small"
            className={classes.dialogField}
            label={t("common.description")}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            defaultValue={editModelParams.description || ""}
          />
          {_id ? null :
          <FormControlLabel
            style={{marginLeft: 0}}
            control={<Checkbox/>}
            label={t("train.is_external")}
            checked={editModelParams.is_external || false}
            onChange={handleCheckboxChange('is_external')}
          />}
          {editModelParams.is_external ? external() : internal()}
        </DialogContent>
        <DialogActions>
          {(is_ext_nb || getRunConfigOption('showASRCheckSampleCmd') && is_ext_asr) && (
            <Button
              disabled={['remote_name'].some(k => !editModelParams[k]) || !getRuntimeServerUrl()}
              onClick={() => setShowCheckSample(true)}
            >
              {t("train.show_check_sample")}
            </Button>
          )}
          <Button onClick={doCancel}>{t("common.cancel")}</Button>
          <Button
            disabled={editModelParams.is_external
              ? ['name', 'ext_type', 'remote_name'].some(k => !editModelParams[k]) || !getRuntimeServerUrl()
              : ['model_config', 'name', 'trainSet', 'type'].some(k => !editModelParams[k]) || !getTrainServerUrl()
                || (!editModelParams.trainSet?.filter(id => id != NO_TRAINSET_ID).length
                    && !editModelParams.no_trainset)}
            onClick={doSave} color="primary" autoFocus
          >
            {t(isClone ? "common.clone" : "common.save")}
          </Button>
        </DialogActions>
      </Dialog>
  );
}

function ExpandableString(props) {
  const { maxlen, data: _d = '' } = props;
  const data = ucfirst(_d);
  const [full, setFull] = useState(false);
  useEffect(() => setFull(false), [data]);
  const fits = data.length <= maxlen;
  return (
    <span onClick={() => setFull(!full)} style={fits ? {} : {cursor: 'pointer'}}>
      {full || !maxlen ? data : data.slice(0,maxlen)+(fits ? '' : ' ...')}
    </span>
  );
}

const mapStateToProps = state => ({
  backends: state.settings.backends,
  projectId: state.settings.projectId,
  projects: state.settings.projects,
  modelFilters: state.modelFilters.filters,
  modelConfigs: state.settings.modelConfigList,
});

export default connect(mapStateToProps)(EditDialog); 
